import SEO from "../components/SEO";

/**
 * Legal document layout with cohesive styling.
 */
export function LegalLayout({ title, subtitle, lastUpdated, children, testid }) {
  return (
    <div className="min-h-screen bg-background" data-testid={testid}>
      <section className="py-16 md:py-20 px-4 md:px-8 border-b border-zinc-900">
        <div className="max-w-3xl mx-auto">
          <p className="text-orange-500 uppercase tracking-[0.3em] text-xs mb-4">Legal</p>
          <h1 className="font-heading text-4xl md:text-6xl uppercase leading-[0.95]">{title}</h1>
          {subtitle && <p className="text-zinc-400 mt-4 text-lg">{subtitle}</p>}
          {lastUpdated && (
            <p className="text-zinc-500 text-sm mt-6">Last updated: {lastUpdated}</p>
          )}
        </div>
      </section>
      <section className="py-12 px-4 md:px-8">
        <article className="max-w-3xl mx-auto legal-content text-zinc-300 leading-relaxed">
          {children}
        </article>
      </section>
    </div>
  );
}

export function Terms() {
  return (
    <>
      <SEO url="/terms" title="Terms of Service" description="Terms of service for Videography Cape Town." />
      <LegalLayout
        title="Terms of Service"
        subtitle="The rules of using Videography Cape Town, written as plainly as we can manage."
        lastUpdated="December 2025"
        testid="terms-page"
      >
        <h2>1. Who we are</h2>
        <p>
          Videography Cape Town ("<strong>we</strong>", "<strong>us</strong>", or "<strong>the
          Platform</strong>") is a South African online directory and marketplace connecting
          creative industry professionals (videographers, editors, cinematographers, sound
          recordists, and related crew) with clients, producers, and advertisers. By
          accessing, browsing, or using the Platform at <strong>videographycapetown.co.za</strong>
          you agree to be bound by these Terms.
        </p>

        <h2>2. Eligibility &amp; Accounts</h2>
        <ul>
          <li>You must be at least 18 years old to register an account.</li>
          <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
          <li>You agree to provide accurate, current information and to keep it up to date.</li>
          <li>One person or registered entity may hold only one account per role (Creative, Client, or Advertiser).</li>
          <li>We reserve the right to suspend or terminate accounts for violations of these Terms.</li>
        </ul>

        <h2>3. Role-specific terms</h2>
        <h3>Creatives</h3>
        <ul>
          <li>Content you upload (portfolio, bio, photos, videos) must be your own work or work you have permission to display.</li>
          <li>You grant us a non-exclusive, royalty-free licence to display your content on the Platform for marketing and discovery purposes.</li>
          <li>Reviews left on your profile by verified clients are protected opinion and cannot be removed except where they breach our policies.</li>
        </ul>

        <h3>Clients &amp; Producers</h3>
        <ul>
          <li>Jobs you post must be real, lawful, and accurate.</li>
          <li>All hiring, payment, contract negotiation and deliverables are between you and the creative. We are not a party to your engagement.</li>
          <li>You agree to respond fairly to applicants and update application statuses within a reasonable time.</li>
        </ul>

        <h3>Advertisers</h3>
        <ul>
          <li>Banner ads are subject to our editorial approval. We reserve the right to reject any banner without detailed explanation, including competitor ads or content that violates South African advertising standards.</li>
          <li>Banner pricing, placement, and dates are as confirmed at the time of booking.</li>
          <li>We do not guarantee a specific number of impressions or clicks, but we commit to transparent reporting via your dashboard and monthly email reports.</li>
          <li>You may cancel a running campaign at any time with <strong>30 days' written notice</strong>. Unused prepaid days will not be refunded.</li>
        </ul>

        <h2>4. Subscriptions &amp; Payments</h2>
        <ul>
          <li>Paid plans (Pro, Premium) are billed in South African Rand (ZAR) on a monthly recurring basis.</li>
          <li>You may cancel at any time. Cancellation takes effect at the end of the current billing cycle (up to 30 days).</li>
          <li>We do not issue pro-rata refunds except where required by the Consumer Protection Act, 2008.</li>
          <li>Failure to pay may result in immediate downgrade to the Free tier.</li>
        </ul>

        <h2>5. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Post false, defamatory, discriminatory, sexually explicit, or unlawful content.</li>
          <li>Scrape, data-mine, or reverse-engineer the Platform.</li>
          <li>Impersonate another person or business.</li>
          <li>Use the messaging feature for spam or unsolicited marketing.</li>
          <li>Attempt to interfere with the security or integrity of the Platform.</li>
        </ul>

        <h2>6. Intellectual Property</h2>
        <p>
          The Videography Cape Town name, logo, site design, and all non-user-generated content
          are the intellectual property of Videography Cape Town and protected under the
          Copyright Act, 1978. User-submitted content remains owned by the user, subject to the
          display licence granted in clause 3.
        </p>

        <h2>7. Disclaimers &amp; Limitation of Liability</h2>
        <p>
          The Platform is provided <strong>"as is"</strong>. We do not vet every creative or
          client personally. Engagement outcomes, quality of work, timeliness of delivery, and
          payment are the responsibility of the parties involved. To the maximum extent
          permitted by South African law, our liability for any claim arising from your use of
          the Platform is limited to the amount you have paid to us in the 12 months preceding
          the claim.
        </p>

        <h2>8. Indemnity</h2>
        <p>
          You agree to indemnify and hold harmless Videography Cape Town, its directors,
          employees and agents from any claim, loss, liability, or expense arising out of your
          breach of these Terms or your violation of any law or third-party right.
        </p>

        <h2>9. Governing Law &amp; Disputes</h2>
        <p>
          These Terms are governed by the laws of the Republic of South Africa. Any dispute
          will be subject to the exclusive jurisdiction of the courts of the Western Cape.
          Before initiating legal proceedings, you agree to first contact us in good faith via{" "}
          <a href="mailto:videographycapetown@gmail.com">videographycapetown@gmail.com</a> to
          seek informal resolution.
        </p>

        <h2>10. Changes to these Terms</h2>
        <p>
          We may update these Terms from time to time. Material changes will be communicated by
          email and/or notice on the Platform at least 7 days before they take effect.
          Continued use after the effective date constitutes acceptance.
        </p>

        <h2>11. Contact</h2>
        <p>
          Videography Cape Town<br />
          Cape Town, Western Cape, South Africa<br />
          Email: <a href="mailto:videographycapetown@gmail.com">videographycapetown@gmail.com</a>
        </p>
      </LegalLayout>
    </>
  );
}

export function Privacy() {
  return (
    <>
      <SEO url="/privacy" title="Privacy Policy" description="How Videography Cape Town collects, uses and protects your information, in line with POPIA." />
      <LegalLayout
        title="Privacy Policy"
        subtitle="How we collect, use and protect your information — in line with the Protection of Personal Information Act (POPIA)."
        lastUpdated="December 2025"
        testid="privacy-page"
      >
        <h2>1. Who is responsible?</h2>
        <p>
          Videography Cape Town is the <strong>Responsible Party</strong> under POPIA for
          personal information collected through the Platform. For any privacy-related request
          or complaint, contact our Information Officer at{" "}
          <a href="mailto:videographycapetown@gmail.com">videographycapetown@gmail.com</a>.
        </p>

        <h2>2. What we collect</h2>
        <h3>Information you give us</h3>
        <ul>
          <li>Account: name, email address, password (hashed), role, company name.</li>
          <li>Profile: bio, skills, services, location, day rate, portfolio images/videos, social links.</li>
          <li>Jobs: title, description, budget, location, deadlines.</li>
          <li>Applications: cover letters, proposed rates.</li>
          <li>Banner ads: creative, destination URL, contact email.</li>
          <li>Contact forms: name, email, message content.</li>
        </ul>
        <h3>Information collected automatically</h3>
        <ul>
          <li>Log data: IP address, browser, pages visited, timestamps.</li>
          <li>Cookies and similar technologies for session management, preferences, and anonymous analytics.</li>
          <li>Ad performance: banner impressions and clicks (aggregated; no personal identifiers tied to individual ad views).</li>
        </ul>

        <h2>3. Why we use your information (lawful basis)</h2>
        <ul>
          <li><strong>Performance of contract:</strong> to operate your account and deliver the services you signed up for.</li>
          <li><strong>Legitimate interest:</strong> to keep the Platform secure, detect abuse, and improve features.</li>
          <li><strong>Consent:</strong> to send optional marketing or newsletters; you can withdraw at any time.</li>
          <li><strong>Legal obligation:</strong> to comply with South African law, tax, or court order.</li>
        </ul>

        <h2>4. Who we share it with</h2>
        <ul>
          <li><strong>Publicly on the Platform:</strong> your creative profile (if you created one), job posts, and public messages.</li>
          <li><strong>Service providers:</strong> hosting (MongoDB Atlas), transactional email (Resend), analytics, payment processors — all bound by data processing agreements.</li>
          <li><strong>Advertisers:</strong> aggregate, anonymised ad performance data only — never your personal identifiers.</li>
          <li><strong>Authorities:</strong> where required by law, regulation, or valid court order.</li>
        </ul>
        <p>We do <strong>not</strong> sell your personal information.</p>

        <h2>5. International transfers</h2>
        <p>
          Some of our service providers may process data outside South Africa. Where this
          happens, we ensure the destination country or provider has an adequate level of
          protection or uses contractual safeguards approved under POPIA / GDPR.
        </p>

        <h2>6. How long we keep it</h2>
        <ul>
          <li>Active accounts: for as long as your account exists.</li>
          <li>Inactive accounts: deleted after 24 months of inactivity, unless you've asked us to keep them.</li>
          <li>Legal records (payments, contracts): 5 years, per South African tax law.</li>
        </ul>

        <h2>7. Your rights under POPIA</h2>
        <ul>
          <li>Access the personal information we hold about you.</li>
          <li>Correct inaccurate information.</li>
          <li>Delete your account and associated data ("right to be forgotten").</li>
          <li>Withdraw consent for marketing at any time.</li>
          <li>Object to processing in certain circumstances.</li>
          <li>Lodge a complaint with the Information Regulator of South Africa (<a href="https://inforegulator.org.za" target="_blank" rel="noopener noreferrer">inforegulator.org.za</a>).</li>
        </ul>
        <p>
          To exercise any of these rights, email{" "}
          <a href="mailto:videographycapetown@gmail.com">videographycapetown@gmail.com</a>.
          We'll respond within 30 days.
        </p>

        <h2>8. Security</h2>
        <p>
          We hash passwords with bcrypt, transmit data over HTTPS/TLS, restrict internal
          access on a need-to-know basis, and review our security practices regularly. No
          system is perfectly secure — but we treat your data the way we'd want ours treated.
        </p>

        <h2>9. Children</h2>
        <p>
          The Platform is not intended for anyone under 18. We do not knowingly collect
          personal information from minors. If you believe we have, contact us and we will
          delete it.
        </p>

        <h2>10. Cookies</h2>
        <p>
          We use essential session cookies to keep you logged in, and anonymous analytics
          cookies to understand how the Platform is used. You can disable cookies in your
          browser — parts of the site (like logging in) may not work without them.
        </p>

        <h2>11. Changes</h2>
        <p>
          We'll notify you by email and on the Platform of any material changes to this policy
          at least 7 days before they take effect.
        </p>
      </LegalLayout>
    </>
  );
}

export function Cookies() {
  return (
    <>
      <SEO url="/cookies" title="Cookie Policy" description="How Videography Cape Town uses cookies and similar technologies." />
      <LegalLayout
        title="Cookie Policy"
        subtitle="The short version: we use the minimum we need."
        lastUpdated="December 2025"
        testid="cookies-page"
      >
        <h2>What's a cookie?</h2>
        <p>
          A cookie is a small text file that a website stores on your device so it can
          remember something about you — like whether you're logged in.
        </p>
        <h2>What we use</h2>
        <ul>
          <li><strong>Session cookie</strong> (<code>session_token</code>): keeps you logged in. Lasts 7 days. Essential; cannot be disabled while using the Platform.</li>
          <li><strong>Preference cookies</strong>: remember search filters and UI preferences on your device only.</li>
          <li><strong>Analytics</strong>: aggregated, anonymous usage data via PostHog to help us improve. No cross-site tracking.</li>
        </ul>
        <h2>How to control cookies</h2>
        <p>
          You can clear or block cookies in your browser settings at any time. See{" "}
          <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">allaboutcookies.org</a> for guides per browser.
        </p>
      </LegalLayout>
    </>
  );
}

export function Refund() {
  return (
    <>
      <SEO url="/refund-policy" title="Refund & Cancellation" description="Refund and cancellation policy for Videography Cape Town subscriptions and banner advertising." />
      <LegalLayout
        title="Refund & Cancellation"
        subtitle="Straight talk about subscriptions and ad bookings."
        lastUpdated="December 2025"
        testid="refund-page"
      >
        <h2>Creative Subscriptions (Pro, Premium)</h2>
        <ul>
          <li>You can cancel anytime from your dashboard.</li>
          <li>Cancellation takes effect at the end of your current billing cycle (up to 30 days).</li>
          <li>No pro-rata refund of unused days, except where required by the Consumer Protection Act, 2008.</li>
          <li>Free tier remains free forever — no auto-upgrade, no surprise charges.</li>
        </ul>

        <h2>Banner Advertising</h2>
        <ul>
          <li>Banner campaigns are booked for a fixed date range agreed at purchase.</li>
          <li>Cancellation requires <strong>30 days' written notice</strong> via <a href="mailto:videographycapetown@gmail.com">videographycapetown@gmail.com</a>.</li>
          <li>Already-served impressions and prepaid days within the notice period are non-refundable.</li>
          <li>If we reject your banner at the editorial review stage, you will be refunded in full.</li>
          <li>If we fail to deliver an agreed campaign due to a fault on our side, you get a pro-rata credit or refund.</li>
        </ul>

        <h2>How to request a refund</h2>
        <p>
          Email <a href="mailto:videographycapetown@gmail.com">videographycapetown@gmail.com</a>{" "}
          from the address on your account. Include your invoice number, plan or banner in
          question, and reason. We respond within 5 working days.
        </p>
      </LegalLayout>
    </>
  );
}
